/* Copyright (C) 2026 Captor Fund Management AB - All Rights Reserved
 * Unauthorized copying or viewing of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Contact andreas.lindh@captor.se for information
 */

import gql from "graphql-tag";

export const POSITIONS_QUERY = gql`
    query positions(
        $filter: PositionFilter
        $groupPositionsBy: PositionSelectorType
        $filterZeroPositions: Boolean
        $lookThrough: Boolean
        $excludeCollateral: Boolean
        $includeAllCashAccounts: Boolean
    ) {
        positions(
            filter: $filter
            groupPositionsBy: $groupPositionsBy
            filterZeroPositions: $filterZeroPositions
            lookThrough: $lookThrough
            excludeCollateral: $excludeCollateral
            includeAllCashAccounts: $includeAllCashAccounts
        ) {
            instrumentId
            instrument {
                name
                isin
                quantityDecimals
                modelType
            }
            portfolioInstrumentId
            externalAccountId
            externalAccount {
                name
            }
            accountId
            account {
                name
            }
            clientId
            client {
                name
                types
            }
            quantity
            amount
            currency
            isCashAccount
        }
    }
`;

export const EXTERNAL_ACCOUNTS_POSITIONS_QUERY = gql`
    query externalAccountsPositions(
        $custodianAccountNumbers: [String!]!
        $externalAccountIds: [GraphQLObjectId!]
        $endDate: GraphQLDateString
        $includeAllCashAccounts: Boolean
    ) {
        externalAccountsPositions(
            custodianAccountNumbers: $custodianAccountNumbers
            externalAccountIds: $externalAccountIds
            endDate: $endDate
            includeAllCashAccounts: $includeAllCashAccounts
        ) {
            instrumentId
            instrument {
                name
                quantityDecimals
            }
            clientId
            client {
                name
            }
            externalAccountId
            externalAccount {
                name
                custodian {
                    _id
                    name
                }
            }
            accountId
            quantity
            amount
            currency
            isCashAccount
        }
    }
`;

/** Intentionally omits required viewFromDate — for schema validation tests only. */
export const CASH_BALANCES_WITHOUT_VIEW_FROM_DATE_QUERY = gql`
    query cashBalancesWithoutViewFromDate($instrumentIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString) {
        cashBalances(instrumentIds: $instrumentIds, endDate: $endDate) {
            instrumentId
        }
    }
`;

export const CASH_BALANCES_QUERY = gql`
    query cashBalances($instrumentIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString, $viewFromDate: GraphQLDateString!) {
        cashBalances(instrumentIds: $instrumentIds, endDate: $endDate, viewFromDate: $viewFromDate) {
            instrumentId
            instrument {
                name
            }
            balanceItems {
                transactionId
                transaction {
                    _id
                    items {
                        amount
                    }
                }
                amount
                balance
            }
        }
    }
`;

export const FUND_UNITS_QUERY = gql`
    query fundUnits($date: GraphQLDateString) {
        fundUnits(date: $date) {
            instrumentId
            instrument {
                name
                isin
            }
            totalUnits
        }
    }
`;

export const ALL_INSTRUMENTS_IN_POSITION_QUERY = gql`
    query allInstrumentsInPosition(
        $clientIds: [GraphQLObjectId!]
        $instrumentIds: [GraphQLObjectId!]
        $clientTypes: [PartyType!]
        $modelTypes: [InstrumentModelTypeEnum!]
        $instrumentPositionType: InstrumentPositionType
        $endDate: GraphQLDateString
    ) {
        allInstrumentsInPosition(
            clientIds: $clientIds
            instrumentIds: $instrumentIds
            clientTypes: $clientTypes
            modelTypes: $modelTypes
            instrumentPositionType: $instrumentPositionType
            endDate: $endDate
        ) {
            instrumentId
            instrument {
                name
            }
            clientIds
            clients {
                name
            }
        }
    }
`;

export const INSTRUMENT_ACCOUNT_POSITIONS_QUERY = gql`
    query instrumentAccountPositions(
        $instrumentIds: [GraphQLObjectId!]!
        $date: GraphQLDateString
        $groupPositionsBy: PositionSelectorType
        $filterZeroPositions: Boolean
    ) {
        instrumentAccountPositions(
            instrumentIds: $instrumentIds
            date: $date
            groupPositionsBy: $groupPositionsBy
            filterZeroPositions: $filterZeroPositions
        ) {
            instrumentId
            instrument {
                name
            }
            externalAccountId
            externalAccount {
                name
            }
            accountId
            account {
                name
            }
            clientId
            client {
                name
            }
            quantity
            currency
        }
    }
`;

export const CASH_LADDER_QUERY = gql`
    query cashLadder($clientIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString, $statusNotIn: [TransactionStatus!]) {
        cashLadder(clientIds: $clientIds, endDate: $endDate, statusNotIn: $statusNotIn) {
            clientId
            client {
                name
            }
            instrumentId
            instrument {
                name
            }
            dates
            balances
            amounts
        }
    }
`;

export const SECURITY_LADDER_QUERY = gql`
    query securityLadder($clientIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString, $statusNotIn: [TransactionStatus!]) {
        securityLadder(clientIds: $clientIds, endDate: $endDate, statusNotIn: $statusNotIn) {
            clientId
            client {
                name
            }
            instrumentId
            instrument {
                name
            }
            dates
            quantities
            positions
        }
    }
`;

export const IMPORT_COLLATERAL_MUTATION = gql`
    mutation importCollateral($input: ImportCollateralInput!) {
        importCollateral(input: $input) {
            _id
        }
    }
`;

export const CREATE_MINI_TRANSACTIONS_MUTATION = gql`
    mutation createMiniTransactions($input: [CreateMiniTransactionInput!]!) {
        createMiniTransactions(input: $input) {
            _id
            carryOwnCostDate
        }
    }
`;

export const INSTRUMENT_POSITIONS_DATA_QUERY = gql`
    query getData($instrumentIds: [GraphQLObjectId!]!, $date: GraphQLDateString, $filterZeroPositions: Boolean) {
        accountPositions: instrumentAccountPositions(
            instrumentIds: $instrumentIds
            date: $date
            groupPositionsBy: AccountId
            filterZeroPositions: $filterZeroPositions
        ) {
            instrumentId
            instrument {
                name
            }
            accountId
            account {
                name
                description
            }
            externalAccountId
            externalAccount {
                name
                description
            }
            clientId
            client {
                name
            }
            quantity
        }
        externalAccountPositions: instrumentAccountPositions(
            instrumentIds: $instrumentIds
            date: $date
            groupPositionsBy: ExternalAccountId
            filterZeroPositions: $filterZeroPositions
        ) {
            instrumentId
            instrument {
                name
            }
            accountId
            account {
                name
                description
            }
            externalAccountId
            externalAccount {
                name
                description
            }
            clientId
            client {
                name
            }
            quantity
        }
    }
`;

export const PORTFOLIO_SWAP_CONSTITUENTS_QUERY = gql`
    query portfolioSwapConstituents($portfolioInstrumentId: GraphQLObjectId!, $date: GraphQLDateString, $filterZeroPositions: Boolean) {
        portfolioSwapConstituents(portfolioInstrumentId: $portfolioInstrumentId, date: $date, filterZeroPositions: $filterZeroPositions) {
            instrumentId
            instrument {
                name
            }
            quantity
            isDebtInstrument
            valuation {
                date
                value {
                    price
                    currency
                }
            }
        }
    }
`;
